import { useEffect, useRef, useMemo } from 'react';
import { createShortcutSlot } from './shortcutRegistry';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface KeyboardShortcut {
  /** Unique identifier */
  id: string;
  /** Key value (e.g. 'Escape', 'n', 's', '/', 'Delete') */
  key: string;
  /** Modifier keys */
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Human-readable label for the help dialog */
  label: string;
  /** Category for grouping in the help dialog */
  category: ShortcutCategory;
  /** Handler - return true to stop propagation */
  /**
   * The event is optional because a shortcut can now be invoked without one:
   * the command palette lists the current screen's shortcuts and runs them on
   * selection, where there is no keystroke to pass. Existing handlers that
   * take `e` keep working — they simply receive undefined from that path, and
   * every one of them either ignores it or guards it.
   */
  handler: (e?: KeyboardEvent) => void | boolean;
  /** If true, the shortcut fires even when an input/textarea is focused */
  allowInInput?: boolean;
  /**
   * Whether this shortcut still fires while a modal is on screen.
   *
   * Defaults to 'yield' — a dialog owns the interaction, and the page behind
   * it should not act on keystrokes aimed at the dialog. Only Escape used to
   * defer, so Ctrl+S pressed inside a Meal Pattern dialog reached Resident
   * Detail's save handler and wrote a DIFFERENT record than the one on screen.
   *
   * Set 'fire' only for a shortcut the modal genuinely should not intercept.
   */
  whenModalOpen?: 'yield' | 'fire';
  /** If true, this shortcut is currently active (default true) */
  enabled?: boolean;
}

export type ShortcutCategory =
  | 'navigation'
  | 'selective-touch'
  | 'data-entry'
  | 'general';

/** Display-friendly category labels */
export const CATEGORY_LABELS: Record<ShortcutCategory, string> = {
  'navigation': 'Navigation',
  'selective-touch': 'Selective Touch / Tray Line',
  'data-entry': 'Data Entry',
  'general': 'General',
} as const;

// ---------------------------------------------------------------------------
// Hook: useKeyboardShortcuts
//
// Key matching, editable-target detection and modal detection all live in
// shortcutRegistry.ts alongside the single dispatcher that uses them. Keeping
// them here would have meant two copies of the matching rules, one of them
// dead.
// ---------------------------------------------------------------------------

export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  enabled = true,
) {
  // Dispatch lives in shortcutRegistry.ts, which owns ONE window listener for
  // the whole app and resolves a key to exactly one handler.
  //
  // Each hook instance used to install its own listener, so two of them
  // binding the same key both fired — `stopPropagation` on a capture-phase
  // window listener stops propagation to other nodes, not to other listeners
  // on the same node. Resident Detail's Ctrl+S and a tab's Ctrl+S therefore
  // both ran, saving two different records from one keystroke, which is why
  // Ctrl+S could not safely be added to more pages. The registry walks slots
  // newest-first so the innermost scope wins instead.
  //
  // The slot is claimed once for the life of the hook and its contents
  // replaced as the list changes. Registering the array itself on every change
  // would loop, because most callers build theirs inline — the registry's
  // signature check is what makes an equivalent re-registration free.
  const slotRef = useRef<ReturnType<typeof createShortcutSlot> | null>(null);
  if (slotRef.current === null) slotRef.current = createShortcutSlot();

  useEffect(() => () => slotRef.current?.release(), []);
  useEffect(() => {
    slotRef.current?.update(shortcuts, enabled);
  }, [shortcuts, enabled]);
}

// ---------------------------------------------------------------------------
// Hook: useGlobalShortcuts - app-wide shortcuts wired into MainLayout
// ---------------------------------------------------------------------------

export interface GlobalShortcutActions {
  onOpenSearch?: () => void;
  onSave?: () => void;
  onNewRecord?: () => void;
  onDelete?: () => void;
  onEscape?: () => void;
}

/**
 * Returns the canonical list of global shortcuts.
 * Pages can merge page-specific shortcuts on top.
 *
 * Note: the "?" / Shift+? help shortcut was intentionally removed — the
 * keyboard-shortcuts reference is now reached from Help → About → Keyboard
 * Shortcuts, so a stray "?" keypress no longer pops the dialog. The dialog is
 * populated from the live registry (see shortcutRegistry.ts), not from a list
 * kept alongside these definitions.
 */
export function buildGlobalShortcuts(actions: GlobalShortcutActions): KeyboardShortcut[] {
  const shortcuts: KeyboardShortcut[] = [];

  // Registered ONLY when the caller has something for it to do. A shortcut on
  // the list is claimed whether or not its handler acts, and a claimed key is
  // a swallowed key — this one used to be registered unconditionally with a
  // handler that no-oped, which is how Escape stopped working app-wide.
  if (actions.onEscape) {
    shortcuts.push({
      id: 'escape-global',
      key: 'Escape',
      label: 'Close dialog / panel / go back',
      category: 'general',
      handler: () => actions.onEscape?.(),
      allowInInput: true,
    });
  }

  if (actions.onOpenSearch) {
    shortcuts.push({
      id: 'search',
      key: '/',
      ctrl: true,
      label: 'Focus search / command palette',
      category: 'navigation',
      handler: () => actions.onOpenSearch!(),
    });
  }

  if (actions.onSave) {
    shortcuts.push({
      id: 'save',
      key: 's',
      ctrl: true,
      label: 'Save current record',
      category: 'data-entry',
      handler: () => actions.onSave!(),
      allowInInput: true,
    });
  }

  if (actions.onNewRecord) {
    shortcuts.push({
      id: 'new-record',
      key: 'n',
      ctrl: true,
      label: 'New record',
      category: 'data-entry',
      handler: () => actions.onNewRecord!(),
    });
  }

  if (actions.onDelete) {
    shortcuts.push({
      id: 'delete',
      key: 'Delete',
      label: 'Delete selected record',
      category: 'data-entry',
      handler: () => actions.onDelete!(),
    });
  }

  return shortcuts;
}

// ---------------------------------------------------------------------------
// Hook: useSelectiveTouchShortcuts - Selective Touch page shortcuts
// ---------------------------------------------------------------------------

export interface SelectiveTouchShortcutActions {
  onNextResident: () => void;
  onPrevResident: () => void;
  onConfirmSelection: () => void;
  onGoBack: () => void;
  /** Navigate between menu items (positive = forward, negative = backward) */
  onNavigateItem: (direction: 1 | -1) => void;
  /** Whether the page is in a state that accepts these shortcuts */
  enabled: boolean;
}

export function buildSelectiveTouchShortcuts(
  actions: SelectiveTouchShortcutActions,
): KeyboardShortcut[] {
  return [
    {
      id: 'st-next-resident',
      key: 'n',
      label: 'Next resident',
      category: 'selective-touch',
      handler: () => actions.onNextResident(),
      enabled: actions.enabled,
    },
    {
      id: 'st-prev-resident',
      key: 'p',
      label: 'Previous resident',
      category: 'selective-touch',
      handler: () => actions.onPrevResident(),
      enabled: actions.enabled,
    },
    {
      id: 'st-confirm',
      key: 'Enter',
      label: 'Confirm / save selection',
      category: 'selective-touch',
      handler: () => actions.onConfirmSelection(),
      enabled: actions.enabled,
    },
    {
      id: 'st-back',
      key: 'Escape',
      label: 'Go back to previous screen',
      category: 'selective-touch',
      handler: () => actions.onGoBack(),
      allowInInput: true,
      enabled: actions.enabled,
    },
    {
      id: 'st-nav-next',
      key: 'Tab',
      label: 'Next menu item',
      category: 'selective-touch',
      handler: () => actions.onNavigateItem(1),
      enabled: actions.enabled,
    },
    {
      id: 'st-nav-prev',
      key: 'Tab',
      shift: true,
      label: 'Previous menu item',
      category: 'selective-touch',
      handler: () => actions.onNavigateItem(-1),
      enabled: actions.enabled,
    },
  ];
}

// ---------------------------------------------------------------------------
// Formatted shortcut key display (for the help dialog)
// ---------------------------------------------------------------------------

/** Get a display-friendly key combo string like "Ctrl+S" or "Shift+?" */
export function formatShortcutKey(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];
  if (shortcut.ctrl) parts.push('Ctrl');
  if (shortcut.alt) parts.push('Alt');
  if (shortcut.shift) parts.push('Shift');

  // Pretty-print special keys
  const keyMap: Record<string, string> = {
    'Escape': 'Esc',
    'Delete': 'Del',
    'Tab': 'Tab',
    'Enter': 'Enter',
    ' ': 'Space',
    '/': '/',
    '?': '?',
  };

  parts.push(keyMap[shortcut.key] ?? shortcut.key.toUpperCase());
  return parts.join(' + ');
}

// ---------------------------------------------------------------------------
// Convenience hook: useSaveShortcut - Ctrl+S to call a save handler
// ---------------------------------------------------------------------------

/**
 * Registers Ctrl+S to trigger a save action.
 * Lightweight convenience so any page with a save button can add it in one line.
 */
export function useSaveShortcut(onSave: () => void, enabled = true) {
  const shortcuts = useMemo<KeyboardShortcut[]>(() => [{
    id: 'page-save',
    key: 's',
    ctrl: true,
    label: 'Save',
    category: 'data-entry',
    handler: () => onSave(),
    allowInInput: true,
    enabled,
  }], [onSave, enabled]);

  useKeyboardShortcuts(shortcuts);
}

// ---------------------------------------------------------------------------
// Convenience hook: useDialogSaveShortcut - Ctrl+S inside a modal
// ---------------------------------------------------------------------------

/**
 * Ctrl+S for a save that lives inside a dialog.
 *
 * A separate hook rather than a flag on `useSaveShortcut`, because getting it
 * wrong is silent. Shortcuts yield to an open modal so the page behind cannot
 * act on a key aimed at the dialog — but a dialog's own shortcut is registered
 * *while* that same modal is open, so the default rule suppresses it and
 * Ctrl+S does nothing at all. Nothing tells you; the key simply has no effect.
 *
 * This sets the opt-in, and being able to name the hook after the situation
 * means callers do not have to remember the interaction.
 *
 * Safe to use inside a dialog that sits over a page which also binds Ctrl+S:
 * the registry resolves a key to the innermost scope, so only this one fires.
 */
export function useDialogSaveShortcut(onSave: () => void, enabled = true) {
  const shortcuts = useMemo<KeyboardShortcut[]>(() => [{
    id: 'dialog-save',
    key: 's',
    ctrl: true,
    label: 'Save and close',
    category: 'data-entry',
    handler: () => onSave(),
    allowInInput: true,
    whenModalOpen: 'fire',
    enabled,
  }], [onSave, enabled]);

  useKeyboardShortcuts(shortcuts);
}
